import { Cell, flexRender } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import { cn } from '@/utils/classnames';
import { ReactNode } from 'react';

export interface CampaignRowLayoutProps {
  actionsCell: Cell<ActiveSourcingChannelDetails, unknown> | undefined;
  channelCell: Cell<ActiveSourcingChannelDetails, unknown> | undefined;
  metricCells: Cell<ActiveSourcingChannelDetails, unknown>[];
  /** Extra content rendered below the channel cell (e.g. the Reach "Channels" expand button) */
  channelExtra?: ReactNode;
  /** Override classes on the left actions panel. Defaults to nothing extra. */
  actionsPanelClassName?: string;
  /** When true, removes the right border from the actions panel. Use for publisher tiles. */
  hideActionsDivider?: boolean;
  /**
   * Tailwind classes applied to each inter-column divider element inside the metric grid.
   * Defaults to `'h-10 top-1/2 -translate-y-1/2'` (fixed 40px, centred) — good for compact
   * publisher tiles. Pass `'top-2 bottom-2'` for full-height-minus-margin dividers on taller
   * parent card rows.
   */
  metricDividerClassName?: string;
  /**
   * Content-only renderer for each metric cell. Receives the cell and its index.
   * Return null to leave that grid track empty (used by scheduled/in-progress cards).
   * The wrapper div, relative positioning, and inter-column divider are always
   * applied by CampaignRowLayout — do not add them here.
   * Defaults to: flexRender(cell.column.columnDef.cell, cell.getContext())
   */
  renderMetricCell?: (
    cell: Cell<ActiveSourcingChannelDetails, unknown>,
    index: number,
  ) => ReactNode;
  /**
   * Extra nodes appended inside the metric grid (e.g. scheduled / publishing spans
   * that use gridColumn to fill hidden metric tracks).
   */
  metricGridExtra?: ReactNode;
  /** Override classes on the outermost flex container */
  className?: string;
}

/**
 * Shared row layout used by both campaign cards (CardContent in cards.tsx) and
 * reach publisher tiles (ReachChannels in reach-channels.tsx).
 *
 * Structure:
 *   [left actions panel] | [w-216 channel section] | [1px divider] | [metric grid]
 *
 * Inter-column dividers inside the metric grid are rendered as absolute-positioned
 * elements with a fixed height so they don't span the full row.
 */
export function CampaignRowLayout({
  actionsCell,
  channelCell,
  metricCells,
  channelExtra,
  actionsPanelClassName,
  hideActionsDivider = false,
  metricDividerClassName = 'h-10 top-1/2 -translate-y-1/2',
  renderMetricCell,
  metricGridExtra,
  className,
}: CampaignRowLayoutProps) {
  return (
    <div className={cn('flex items-stretch', className)}>
      {/* Left panel: toggle+menu for parent cards, return arrow for publisher tiles */}
      {actionsCell && (
        <div
          className={cn(
            'flex shrink-0 flex-col items-center justify-center self-stretch p-4',
            !hideActionsDivider && 'border-fx-neutral-200 border-r',
            actionsPanelClassName,
          )}
        >
          {flexRender(
            actionsCell.column.columnDef.cell,
            actionsCell.getContext(),
          )}
        </div>
      )}

      {/* Main content: channel section + divider + metric grid */}
      <div className="flex min-w-0 flex-1 items-stretch">
        {channelCell && (
          <div className="flex w-[256px] shrink-0 flex-col items-start justify-center gap-1 px-4 py-3">
            {flexRender(
              channelCell.column.columnDef.cell,
              channelCell.getContext(),
            )}
            {channelExtra}
          </div>
        )}

        <div className="bg-fx-neutral-200 my-4 w-px shrink-0" />

        {/*
         * CSS Grid for metric columns — equal widths regardless of cell count.
         * Grid uses default align-items: stretch so each cell fills the full row height.
         * This lets the absolute-positioned divider use top/bottom offsets that are
         * relative to the full row height rather than the cell's content height.
         * justify-center on the cell keeps content vertically centred.
         */}
        <div
          className="grid min-w-0 flex-1"
          style={{
            gridTemplateColumns: metricCells
              .map(cell => `minmax(${cell.column.getSize()}px, 1fr)`)
              .join(' '),
          }}
        >
          {metricCells.map((cell, index) => {
            const content = renderMetricCell
              ? renderMetricCell(cell, index)
              : flexRender(cell.column.columnDef.cell, cell.getContext());

            if (content === null) return null;

            return (
              <div
                key={cell.id}
                className="[&_p]:!text-fx-neutral-600 relative flex flex-col items-center justify-center gap-2 px-4 py-3"
              >
                {/* Inter-column divider — absolute so it doesn't affect cell height */}
                {index > 0 && (
                  <div
                    className={cn(
                      'bg-fx-neutral-200 absolute left-0 w-px',
                      metricDividerClassName,
                    )}
                  />
                )}
                {content}
              </div>
            );
          })}
          {metricGridExtra}
        </div>
      </div>
    </div>
  );
}
